import { useLocation, useNavigate } from "react-router-dom";

import { CircleArrowRight, LogOut } from "lucide-react";

import { useStoreZustandUserAuth } from "@/store-zustand/user-auth";

import Image from "@/common/assets/png/logo-let-scom.png";
import { abrirBackoffice } from "@/common/navigation/abrir-backoffice";
import {
  getMenusByFeatures,
  type SidebarMenuItem,
  splitMenusByType,
} from "@/common/navigation/menus-by-feature";

function MobileMenu({ isOpen, closeMenu }: { isOpen: boolean; closeMenu: () => void }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { features, logout, setUser, setIsAuthenticated } = useStoreZustandUserAuth();

  const currentRoute = location.pathname;
  const { internalMenus, externalMenus } = splitMenusByType(getMenusByFeatures(features));

  const handleNavigation = (menu: SidebarMenuItem) => {
    if (menu.external) {
      if (menu.abrirComSessao) void abrirBackoffice(menu.link);
      else window.open(menu.link, "_blank", "noopener,noreferrer");
      closeMenu();
      return;
    }

    navigate(menu.link);
    closeMenu();
  };

  const handleLogout = () => {
    navigate("/");
    logout();
    setUser(null);
    setIsAuthenticated(false);
    closeMenu();
  };

  const renderMenuItem = (menu: SidebarMenuItem, showActiveState = true) => {
    const isActive = showActiveState
      ? menu.subRoutes
        ? menu.subRoutes.some((route) => currentRoute === route)
        : currentRoute === menu.link
      : false;

    return (
      <li
        key={menu.id}
        onClick={() => handleNavigation(menu)}
        className={`flex cursor-pointer items-center gap-x-4 rounded-md p-3 text-gray-300 hover:bg-gray-700 ${
          isActive ? "bg-gray-800" : ""
        }`}
      >
        <span className="text-xl">{menu.icon}</span>
        <span className="text-base">{menu.title}</span>
      </li>
    );
  };

  return (
    <div
      className={`fixed left-0 top-0 z-50 h-full w-64 bg-gray-custom900 transition-transform duration-500 ${
        isOpen ? "translate-x-0" : "-translate-x-full"
      }`}
    >
      <div className="flex items-center justify-between border-b border-gray-700 p-4">
        <img src={Image} alt="Logo" className="h-16 w-16" />
        <CircleArrowRight className="cursor-pointer text-2xl text-white" onClick={closeMenu} />
      </div>

      <ul className="mt-4">{internalMenus.map((menu) => renderMenuItem(menu))}</ul>

      <div className="absolute bottom-4 w-full px-4">
        {externalMenus.length > 0 && (
          <ul className="mb-2 border-t border-gray-700 pt-2">
            {externalMenus.map((menu) => renderMenuItem(menu, false))}
          </ul>
        )}

        <li
          onClick={handleLogout}
          className="flex cursor-pointer items-center gap-x-4 rounded-md p-3 text-gray-300 hover:bg-gray-700"
        >
          <LogOut />
          <span className="text-base">Sair</span>
        </li>
      </div>
    </div>
  );
}

export default MobileMenu;
